import { Navigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Spinner } from '@/components/ui';
import type { UserRole } from '@/lib/navigation';

interface RoleBasedRouteProps {
  children: React.ReactNode;
  requiredRoles: UserRole[];
  redirectTo?: string;
}

/**
 * Route guard that checks if the user has one of the required roles.
 * Redirects to the specified path (default: '/') if the user doesn't have access.
 *
 * Note: This component assumes the user is already authenticated.
 * Use inside ProtectedRoute or AppLayout for proper auth checking.
 */
export function RoleBasedRoute({
  children,
  requiredRoles,
  redirectTo = '/',
}: Readonly<RoleBasedRouteProps>) {
  const { user, isLoading } = useAuth();

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Spinner size="lg" />
      </div>
    );
  }

  // If no user or user's role is not in required roles, redirect
  if (!user || !requiredRoles.includes(user.role)) {
    return <Navigate to={redirectTo} replace />;
  }

  return <>{children}</>;
}

